import { ChangeEvent, useState } from 'react';
import FormField from '@/common/PassengerTools/FormField';
import {
  Button as StyledButton,
  Input,
  Select
} from '../PassengerTools.styles';
import {
  TradeOptions,
  WSYS_SCENARIOS,
  addTradeToCurrentUser,
  getCurrentSessionUser,
  setSessionUser,
  storeTestUserInLocalStorage
} from '../utils/createTestUser';

type VinMode = 'default' | 'manual' | 'wsys';

export type TradeConfig = {
  equityLevel: TradeOptions['equityLevel'];
  loanAmount: string;
  manualVin: string;
  status: TradeOptions['status'];
  vinMode: VinMode;
  wsysScenario: keyof typeof WSYS_SCENARIOS;
};

export const DEFAULT_TRADE_CONFIG: TradeConfig = {
  equityLevel: 'Positive',
  loanAmount: '',
  manualVin: '',
  status: 'active',
  vinMode: 'default',
  wsysScenario: 'Incentive And Tax Savings'
};

export function getTradeVin(config: TradeConfig): string | undefined {
  if (config.status === 'pending' || config.vinMode === 'manual')
    return config.manualVin || undefined;
  if (config.vinMode === 'wsys') return WSYS_SCENARIOS[config.wsysScenario];

  return undefined;
}

export function isPendingMissingVin(config: TradeConfig): boolean {
  return config.status === 'pending' && !config.manualVin;
}

type TradeFieldsProps = {
  allowExpired: boolean;
  config: TradeConfig;
  idPrefix: string;
  label?: string;
  onChange: (config: TradeConfig) => void;
};

function TradeFields({
  allowExpired,
  config,
  idPrefix,
  label,
  onChange
}: TradeFieldsProps) {
  const showVin = config.status === 'active' || config.status === 'pending';
  const vinRequired = config.status === 'pending';
  const showEquity = config.status === 'active';
  const showLoanAmount =
    config.equityLevel === 'Negative' && config.status === 'active';

  return (
    <div className="flex flex-col gap-8">
      {label && (
        <div className="text-sm font-medium text-gray-300">{label}</div>
      )}

      <FormField label="Status" htmlFor={`${idPrefix}-status`}>
        <Select
          id={`${idPrefix}-status`}
          value={config.status}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            onChange({
              ...DEFAULT_TRADE_CONFIG,
              status: e.target.value as TradeConfig['status']
            })
          }
        >
          <option value="active">Active</option>
          {allowExpired && <option value="expired">Expired</option>}
          <option value="pending">Pending</option>
        </Select>
      </FormField>
      {config.status === 'expired' && (
        <div className="text-xs text-amber-300">
          {
            "Expired requires Create & Login — Create alone won't finish the expire step."
          }
        </div>
      )}

      {showEquity && (
        <FormField label="Equity Level" htmlFor={`${idPrefix}-equity`}>
          <Select
            id={`${idPrefix}-equity`}
            value={config.equityLevel}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              onChange({
                ...config,
                equityLevel: e.target.value as TradeConfig['equityLevel'],
                loanAmount: ''
              })
            }
          >
            <option value="Positive">Positive</option>
            <option value="High Positive">High Positive</option>
            <option value="Negative">Negative</option>
          </Select>
        </FormField>
      )}

      {showLoanAmount && (
        <FormField
          label="Loan Amount ($)"
          htmlFor={`${idPrefix}-loan-amount`}
          helperText="Loan amount for negative equity (2015 Chevrolet Equinox ~$100 value)"
        >
          <Input
            id={`${idPrefix}-loan-amount`}
            type="number"
            value={config.loanAmount}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange({ ...config, loanAmount: e.target.value })
            }
            placeholder="25000"
            min="1"
            max="100000"
          />
        </FormField>
      )}

      {showVin && (
        <div className="flex flex-col gap-8">
          <FormField label="VIN" htmlFor={`${idPrefix}-vin-mode`}>
            <Select
              id={`${idPrefix}-vin-mode`}
              value={vinRequired ? 'manual' : config.vinMode}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onChange({
                  ...config,
                  manualVin: '',
                  vinMode: e.target.value as VinMode
                })
              }
            >
              {!vinRequired && (
                <option value="default">
                  {config.equityLevel === 'High Positive'
                    ? 'Default (2023 Cadillac Escalade)'
                    : 'Default (2015 Chevrolet Equinox)'}
                </option>
              )}
              <option value="manual">Manual VIN</option>
              {!vinRequired && <option value="wsys">WSYS VIN</option>}
            </Select>
          </FormField>

          {(config.vinMode === 'manual' || vinRequired) && (
            <div className="flex items-center gap-8">
              <FormField label="VIN" htmlFor={`${idPrefix}-manual-vin`}>
                <Input
                  id={`${idPrefix}-manual-vin`}
                  type="text"
                  value={config.manualVin}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChange({ ...config, manualVin: e.target.value })
                  }
                  placeholder="Enter 17-character VIN"
                  minLength={17}
                  maxLength={17}
                />
              </FormField>
              <a
                href="https://vingenerator.org/year-brand-model"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-blue-300 hover:underline text-sm"
                data-analytics-id={null}
                data-testid={`${idPrefix}-vin-generator`}
              >
                Generator
              </a>
            </div>
          )}

          {config.vinMode === 'wsys' && !vinRequired && (
            <FormField
              label="WSYS Scenario"
              htmlFor={`${idPrefix}-wsys-scenario`}
            >
              <Select
                id={`${idPrefix}-wsys-scenario`}
                value={config.wsysScenario}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  onChange({
                    ...config,
                    wsysScenario: e.target.value as keyof typeof WSYS_SCENARIOS
                  })
                }
              >
                {Object.keys(WSYS_SCENARIOS).map(key => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </Select>
            </FormField>
          )}

          {vinRequired && !config.manualVin && (
            <div className="text-sm text-orange-400">
              VIN is required for pending trades (use a VIN Carvana cannot
              appraise)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type OwnProps = {
  addSecondTrade: boolean;
  applyOnCreate: boolean;
  isLoggedIn: boolean;
  isOpen: boolean;
  onAddSecondTradeChange: (add: boolean) => void;
  onApplyOnCreateChange: (apply: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onTrade1Change: (config: TradeConfig) => void;
  onTrade2Change: (config: TradeConfig) => void;
  trade1: TradeConfig;
  trade2: TradeConfig;
};

export default function TradeSection({
  addSecondTrade,
  applyOnCreate,
  isLoggedIn,
  isOpen,
  onAddSecondTradeChange,
  onApplyOnCreateChange,
  onOpenChange,
  onTrade1Change,
  onTrade2Change,
  trade1,
  trade2
}: OwnProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [results, setResults] = useState<string[]>([]);

  const canAdd =
    isLoggedIn &&
    !isAdding &&
    !isPendingMissingVin(trade1) &&
    (!addSecondTrade || !isPendingMissingVin(trade2));

  async function handleAddTrade(reload: boolean) {
    setIsAdding(true);
    setResults([]);
    setErrorMessage('');
    try {
      const ids: string[] = [];

      ids.push(
        await addTradeToCurrentUser({
          equityLevel: trade1.equityLevel,
          loanAmount: trade1.loanAmount
            ? parseInt(trade1.loanAmount, 10)
            : undefined,
          status: trade1.status,
          vin: getTradeVin(trade1)
        })
      );

      if (addSecondTrade) {
        ids.push(
          await addTradeToCurrentUser({
            equityLevel: trade2.equityLevel,
            loanAmount: trade2.loanAmount
              ? parseInt(trade2.loanAmount, 10)
              : undefined,
            status: trade2.status,
            vin: getTradeVin(trade2)
          })
        );
      }

      const sessionUser = getCurrentSessionUser();
      if (sessionUser && ids.length > 0) {
        const existing =
          sessionUser.acquisitionIds ??
          (sessionUser.acquisitionId ? [sessionUser.acquisitionId] : []);
        const merged = [...existing, ...ids];
        const updated = {
          ...sessionUser,
          acquisitionId: merged[0],
          acquisitionIds: merged
        };
        setSessionUser(updated);
        storeTestUserInLocalStorage(updated);
      }

      setResults(ids);
      if (reload) window.location.reload();
    } catch (error) {
      setErrorMessage(`Failed to apply trade: ${error}`);
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="flex flex-col items-start">
      <button
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="inline-flex items-center gap-8 rounded bg-gray-600 px-lg py-md text-white hover:bg-gray-700"
        data-analytics-id={null}
        data-testid="toggle-trade"
      >
        <span className="text-sm font-medium">Trade-in</span>
        {results.length > 0 && !isOpen && (
          <span className="text-xs text-green-400">
            &#10003;{' '}
            {results.length > 1 ? `${results.length} Applied` : 'Applied'}
          </span>
        )}
        <span className="text-sm">{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && (
        <div className="self-stretch flex flex-col gap-8 border border-gray-600 rounded-b p-sm">
          {!isLoggedIn && (
            <label
              htmlFor="trade-apply-on-create"
              className="flex items-center gap-8 text-sm text-white"
            >
              <input
                type="checkbox"
                id="trade-apply-on-create"
                checked={applyOnCreate}
                onChange={e => onApplyOnCreateChange(e.target.checked)}
                className="h-16 w-16"
              />
              Apply on Create
            </label>
          )}
          <TradeFields
            allowExpired={!isLoggedIn}
            config={trade1}
            idPrefix="trade1"
            label={addSecondTrade ? 'Trade 1' : undefined}
            onChange={next => {
              if (next.status === 'expired' && addSecondTrade) {
                onAddSecondTradeChange(false);
              }
              onTrade1Change(next);
            }}
          />

          {trade1.status !== 'expired' && (
            <div className="flex items-center gap-8">
              <input
                type="checkbox"
                id="add-second-trade"
                checked={addSecondTrade}
                onChange={e => onAddSecondTradeChange(e.target.checked)}
                className="h-16 w-16"
              />
              <label htmlFor="add-second-trade" className="text-white">
                Add Second Trade
              </label>
            </div>
          )}

          {addSecondTrade && (
            <div className="border border-gray-700 rounded p-sm">
              <TradeFields
                allowExpired={false}
                config={trade2}
                idPrefix="trade2"
                label="Trade 2"
                onChange={onTrade2Change}
              />
            </div>
          )}

          {results.length > 0 && (
            <div
              aria-live="polite"
              className="text-green-400 text-sm"
              role="status"
            >
              <div>
                Trade{results.length > 1 ? 's' : ''} applied successfully.
              </div>
              {results.map((id, i) => (
                <div key={id}>
                  {results.length > 1
                    ? `Trade ${i + 1} ID: `
                    : 'Acquisition ID: '}
                  {id}
                </div>
              ))}
            </div>
          )}
          {errorMessage && (
            <div className="text-[#feaaaa] text-sm" role="alert">
              {errorMessage}
            </div>
          )}

          {isLoggedIn && (
            <div className="flex flex-wrap gap-8">
              <StyledButton
                type="button"
                onClick={() => handleAddTrade(false)}
                data-analytics-id={null}
                disabled={!canAdd}
                data-testid="add-trade"
              >
                {isAdding
                  ? 'Applying...'
                  : addSecondTrade
                  ? 'Apply Trades'
                  : 'Apply Trade'}
              </StyledButton>
              <StyledButton
                type="button"
                onClick={() => handleAddTrade(true)}
                data-analytics-id={null}
                disabled={!canAdd}
                data-testid="add-trade-and-update"
              >
                {isAdding
                  ? 'Applying...'
                  : addSecondTrade
                  ? 'Apply Trades & Update'
                  : 'Apply Trade & Update'}
              </StyledButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
